import { useState } from "react";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { ChevronUp } from "lucide-react";
import { asTrimmedString } from "@/modules/zoom-calls/components/analysis.helpers";
import type { Requirement } from "@/modules/zoom-calls/types";
import RequirementChips from "./requirement-chips";
import PitchCard from "./pitch-card";

const RequirementCard = ({
  requirement,
  index,
}: {
  requirement: Requirement;
  index: number;
}) => {
  const theme = useTheme();
  const p = theme.nawy.colors.primitives;
  const [collapsed, setCollapsed] = useState(false);

  const pitches = Array.isArray(requirement.pitches) ? requirement.pitches : [];
  const label = asTrimmedString(requirement.label);

  return (
    <Box
      sx={{
        borderRadius: "12px",
        // Same grey as the sibling summary/outcome cards (Figma "Call details").
        backgroundColor: p.white[200],
        p: "12px",
        display: "flex",
        flexDirection: "column",
        gap: theme.nawy.spacing.xs,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: "999px",
              backgroundColor: p.navy[800],
              color: theme.nawy.colors.semantic.text.onColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {index + 1}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 400,
                lineHeight: "16px",
                color: theme.nawy.colors.semantic.text.tertiary,
                whiteSpace: "nowrap",
              }}
            >
              Client request
            </Typography>
            {label && (
              <Typography
                sx={{
                  fontSize: 11,
                  color: p.grey[700],
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? "Expand" : "Collapse"}
          sx={{ p: "2px", color: p.grey[600] }}
        >
          <ChevronUp
            size={16}
            style={{
              transform: collapsed ? "rotate(180deg)" : "none",
              transition: "transform 0.15s ease",
            }}
          />
        </IconButton>
      </Box>

      {!collapsed && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: theme.nawy.spacing.sm,
          }}
        >
          <RequirementChips requirement={requirement} />
          {pitches.map((pitch, j) => (
            <PitchCard key={j} pitch={pitch} index={j} total={pitches.length} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default RequirementCard;
